<template>
  <div class="w-full flex flex-col fade-in">
    <!-- Full Name Input -->
    <BaseText type="label" size="md" class="text-text-muted w-full mb-1">
      Full Name
    </BaseText>
    <div class="input-container w-full flex items-center" :class="{'active': fullName?.length || fullNameInputFocused, 'errored': fullNameError && !fullNameInputFocused}">
      <input v-model="fullName" class="form-input flex-grow min-w-0" placeholder="Zark Muckerberg" type="text"
      @focus="fullNameInputFocused = true" @blur="fullNameInputFocused = false" @input="() => submitAttempted = false" />
      <transition>
        <InfoIcon v-if="!fullNameInputFocused && fullNameError" style="color: #DF1C41" />
      </transition>
    </div>
    <transition name="error">
      <div v-if="fullNameError" class="w-full flex items-end h-7">
        <BaseText type="body" size="sm" class="transition-colors" :style="{ color: fullNameInputFocused ? '#6B7280' : '#DF1C41' }">
          {{ fullNameError }}
        </BaseText>
      </div>
    </transition>

    <!-- Email Input -->
    <BaseText type="label" size="md" class="text-text-muted w-full mt-4 mb-1">
      Email
    </BaseText>
    <div class="input-container w-full flex items-center" :class="{'active': email?.length || emailInputFocused, 'errored': emailError && !emailInputFocused}">
      <input v-model="email" class="form-input flex-grow min-w-0" placeholder="hello@yourcompany.com" type="email"
      @focus="emailInputFocused = true" @blur="emailInputFocused = false" @input="() => { submitAttempted = false; emailIsDisposable = false }" />
      <transition>
        <InfoIcon v-if="!emailInputFocused && emailError" style="color: #DF1C41" />
      </transition>
    </div>
    <transition name="error">
      <div v-if="emailError" class="w-full flex items-end h-7">
        <BaseText type="body" size="sm" class="transition-colors" :style="{ color: emailInputFocused ? '#6B7280' : '#DF1C41' }">
          {{ emailError }}
        </BaseText>
      </div>
    </transition>

    <!-- Password Input -->
    <BaseText type="label" size="md" class="text-text-muted w-full mt-4 mb-1">
      Password
    </BaseText>
    <div class="input-container w-full flex items-center mb-2" :class="{'active': password?.length || passwordInputFocused}">
      <input v-model="password" class="form-input flex-grow min-w-0" placeholder="Choose a password" :type="showPassword ? 'text' : 'password'"
      @focus="passwordInputFocused = true" @blur="passwordInputFocused = false" @input="() => submitAttempted = false"/>
      <button class="w-6 h-6 relative flex items-center cursor-pointer flex-shrink-0 text-text-disabled"
      type="button" tabindex="-1" @click="showPassword= !showPassword">
        <transition name="visibility">
          <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" key="visible-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" class="h-4">
            <path d="M9.16536 4.42071C8.76872 4.5401 8.54397 4.95841 8.66335 5.35505C8.78273 5.75169 9.20105 5.97645 9.59768 5.85706L9.16536 4.42071ZM21.529 11.1176L22.1806 10.7462V10.7462L21.529 11.1176ZM19.1413 14.9666C18.8678 15.2776 18.8982 15.7515 19.2092 16.0251C19.5203 16.2986 19.9942 16.2682 20.2677 15.9571L19.1413 14.9666ZM21.5297 12.8811L20.8781 12.5098L20.8781 12.5098L21.5297 12.8811ZM3.28029 2.21967C2.9874 1.92678 2.51253 1.92678 2.21963 2.21967C1.92674 2.51256 1.92674 2.98744 2.21963 3.28033L3.28029 2.21967ZM2.47016 11.1188L3.12179 11.4901H3.12179L2.47016 11.1188ZM20.7196 21.7803C21.0125 22.0732 21.4874 22.0732 21.7803 21.7803C22.0732 21.4874 22.0732 21.0126 21.7803 20.7197L20.7196 21.7803ZM2.47125 12.8829L1.81966 13.2543H1.81966L2.47125 12.8829ZM10.2322 10.2322C10.5251 9.93934 10.5251 9.46447 10.2322 9.17157C9.9393 8.87868 9.46443 8.87868 9.17153 9.17157L10.2322 10.2322ZM14.8284 14.8284C15.1213 14.5355 15.1213 14.0607 14.8284 13.7678C14.5355 13.4749 14.0606 13.4749 13.7677 13.7678L14.8284 14.8284ZM9.59768 5.85706C13.5394 4.67067 17.9939 6.42962 20.8774 11.489L22.1806 10.7462C19.0295 5.21732 13.9174 2.99041 9.16536 4.42071L9.59768 5.85706ZM20.2677 15.9571C20.9622 15.1674 21.6043 14.2652 22.1814 13.2524L20.8781 12.5098C20.3482 13.4398 19.7644 14.258 19.1413 14.9666L20.2677 15.9571ZM20.8774 11.489C21.0579 11.8057 21.058 12.1942 20.8781 12.5098L22.1814 13.2524C22.6242 12.4754 22.6229 11.5222 22.1806 10.7462L20.8774 11.489ZM2.21963 3.28033L5.99934 7.06003L7.06 5.99937L3.28029 2.21967L2.21963 3.28033ZM3.12179 11.4901C4.21375 9.57383 5.53387 8.13258 6.95695 7.14609L6.10238 5.91332C4.47301 7.04281 3.00749 8.66097 1.81853 10.7475L3.12179 11.4901ZM5.99934 7.06003L16.9399 18.0006L18.0005 16.9399L7.06 5.99937L5.99934 7.06003ZM16.9399 18.0006L20.7196 21.7803L21.7803 20.7197L18.0005 16.9399L16.9399 18.0006ZM1.81966 13.2543C3.74764 16.6368 6.40542 18.7894 9.29529 19.6173C12.1917 20.4471 15.2481 19.9232 17.8975 18.0866L17.0429 16.8539C14.7434 18.4479 12.1496 18.8747 9.7084 18.1753C7.26065 17.4741 4.8934 15.6178 3.12283 12.5115L1.81966 13.2543ZM1.81853 10.7475C1.37557 11.5249 1.37752 12.4786 1.81966 13.2543L3.12283 12.5115C2.94218 12.1946 2.9421 11.8055 3.12179 11.4901L1.81853 10.7475ZM12 14.5C10.6192 14.5 9.49996 13.3807 9.49996 12H7.99996C7.99996 14.2091 9.79082 16 12 16V14.5ZM9.49996 12C9.49996 11.3094 9.77896 10.6855 10.2322 10.2322L9.17153 9.17157C8.4485 9.89461 7.99996 10.8956 7.99996 12H9.49996ZM13.7677 13.7678C13.3145 14.221 12.6905 14.5 12 14.5V16C13.1043 16 14.1054 15.5515 14.8284 14.8284L13.7677 13.7678Z"
            fill="currentColor" />
          </svg>
          <svg v-else-if="password?.length" xmlns="http://www.w3.org/2000/svg" key="hide-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" class="h-4">
            <path d="M2.47032 11.1187L1.81869 10.7474H1.81869L2.47032 11.1187ZM21.5298 11.1188L22.1814 10.7475V10.7475L21.5298 11.1188ZM2.47032 12.8811L1.81869 13.2524H1.81869L2.47032 12.8811ZM21.5298 12.8812L22.1814 13.2526V13.2526L21.5298 12.8812ZM3.12194 11.49C5.43733 7.42677 8.76885 5.49998 12 5.5C15.2312 5.50002 18.5627 7.42687 20.8781 11.4901L22.1814 10.7475C19.6579 6.31907 15.8791 4.00003 12 4C8.12093 3.99997 4.34216 6.31896 1.81869 10.7474L3.12194 11.49ZM1.81869 13.2524C4.34216 17.6809 8.12092 19.9999 12 19.9999C15.8791 20 19.6579 17.681 22.1814 13.2526L20.8781 12.5099C18.5627 16.5732 15.2312 18.5 12 18.4999C8.76886 18.4999 5.43733 16.5731 3.12194 12.5098L1.81869 13.2524ZM1.81869 10.7474C1.37619 11.5239 1.37619 12.4759 1.81869 13.2524L3.12194 12.5098C2.94177 12.1936 2.94177 11.8062 3.12194 11.49L1.81869 10.7474ZM20.8781 11.4901C21.0583 11.8063 21.0583 12.1937 20.8781 12.5099L22.1814 13.2526C22.6239 12.476 22.6239 11.524 22.1814 10.7475L20.8781 11.4901ZM14.5 12C14.5 13.3807 13.3807 14.5 12 14.5V16C14.2092 16 16 14.2091 16 12H14.5ZM12 14.5C10.6193 14.5 9.50004 13.3807 9.50004 12H8.00004C8.00004 14.2091 9.7909 16 12 16V14.5ZM9.50004 12C9.50004 10.6193 10.6193 9.5 12 9.5V8C9.7909 8 8.00004 9.79086 8.00004 12H9.50004ZM12 9.5C13.3807 9.5 14.5 10.6193 14.5 12H16C16 9.79086 14.2092 8 12 8V9.5Z"
            fill="currentColor" />
          </svg>
        </transition>
      </button>
    </div>
    <ValidationVisualizer
      theme="light"
      :value="password"
      :validation-requirements="validationRequirements"
      :is-valid.sync="passwordValid"
      :show-validator="!!(password?.length) || submitAttempted"
      :top-padding="0"
      :bottom-padding="8"
    />

    <!-- Sign Up Button -->
    <button
      class="submit-button w-full mt-4 p-2 h-10 flex items-center justify-center transition duration-200"
      :class="{ disabled: submitDisabled }"
      type="button"
      :disabled="loading"
      @click="handleSignup"
    >
      <BaseLoadingSpinnerCircle v-if="loading" :duration="1" class="text-white" />
      <BaseText
        v-else
        type="heading"
        size="sm"
        class="text-white"
      >
        Sign up to join
      </BaseText>
    </button>

    <!-- reCAPTCHA -->
    <transition name="recaptcha">
      <div v-show="showRecaptcha" class="relative w-full" style="height: 102px">
        <div
          v-if="recaptchaLoading"
          class="absolute inset-0 flex items-center justify-center"
        >
          <BaseLoadingSpinnerCircle class="text-text-muted" />
        </div>
        <div
          id="recaptcha-container-signup"
          ref="recaptchaContainer"
          class="pt-6 w-full flex justify-center"
          :class="{ 'opacity-0': recaptchaLoading }"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import firebase from '@/api/config/FirebaseConfig'
import ForeplayAPI from '@/api/foreplayServer'
import disposableEmailBlocklist from '@/utils/disposableEmailBlocklist'
import ValidationVisualizer from '@/components/account/ValidationVisualizer.vue'
import InfoIcon from '@/components/globals/Icons/InfoSimpleIcon.vue'

const RECAPTCHA_INIT_DELAY_MS = 400
const RECAPTCHA_RETRY_DELAY_MS = 1000

export default {
  name: 'TeamInviteSignUpForm',
  components: {
    ValidationVisualizer,
    InfoIcon
  },
  props: {
    inviteEmail: {
      type: String,
      default: ''
    }
  },
  data () {
    const validationRequirements = [
      {
        label: 'At least 8 characters.',
        validator: (password) => password?.length && password.length >= 8,
      },
      {
        label: 'At least 1 number.',
        validator: (password) => /[0-9]/.test(password),
      },
      {
        label: 'At least 1 uppercase letter.',
        validator: (password) => /[A-Z]/.test(password),
      }
    ]

    return {
      fullName: null,
      email: this.inviteEmail || null,
      password: null,
      showPassword: false,
      emailIsDisposable: false,
      passwordValid: false,
      validationRequirements,
      
      recaptchaVerified: false,
      recaptchaVerifier: null,
      recaptchaWidgetId: null,
      recaptchaSetupDone: false,
      recaptchaLoading: false,
      recaptchaRetryTimeout: null,
      recaptchaRetryCount: 0,
      
      loading: false,
      submitAttempted: false,
      fullNameInputFocused: false,
      emailInputFocused: false,
      passwordInputFocused: false
    }
  },
  computed: {
    submitDisabled () {
      return this.loading || !this.recaptchaVerified || this.hasErrors || !this.fullName?.length || !this.email?.length || !this.password?.length
    },
    fullNameError () {
      return this.submitAttempted && !this.fullName?.length ? 'Full Name required.' : null
    },
    emailError () {
      if (!this.email?.length) {
        return this.submitAttempted ? 'Email required.' : null
      }
      const emailRegex = /^[^\s@]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
      if (!emailRegex.test(this.email) || this.emailIsDisposable) return 'Please enter a valid email address.'

      return null
    },
    hasErrors () {
      return this.fullNameError || this.emailError || !this.passwordValid
    },
    showRecaptcha () {
      return this.fullName?.length && this.email?.length && this.password?.length && !this.hasErrors
    }
  },
  watch: {
    showRecaptcha (visible) {
      if (visible && !this.recaptchaSetupDone && !this.recaptchaLoading) {
        this.initRecaptcha()
      }
    }
  },
  beforeDestroy () {
    if (this.recaptchaRetryTimeout) {
      clearTimeout(this.recaptchaRetryTimeout)
      this.recaptchaRetryTimeout = null
    }
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear()
      this.recaptchaVerifier = null
    }
  },
  methods: {
    waitForRecaptchaMount () {
      return new Promise((resolve) => {
        setTimeout(resolve, RECAPTCHA_INIT_DELAY_MS)
      })
    },
    async initRecaptcha () {
      if (this.recaptchaLoading || this.recaptchaSetupDone) return

      this.recaptchaLoading = true

      try {
        await this.$nextTick()
        await this.waitForRecaptchaMount()

        if (this._isDestroyed || !this.showRecaptcha) {
          this.recaptchaLoading = false
          return
        }

        await this.setupRecaptcha()

        if (this._isDestroyed) return

        this.recaptchaSetupDone = true
        this.recaptchaRetryCount = 0
        this.recaptchaLoading = false
      } catch (error) {
        console.error(error)

        if (this._isDestroyed) return

        const canRetry = this.showRecaptcha && this.recaptchaRetryCount < 1
        if (canRetry) {
          this.recaptchaRetryCount += 1
          this.recaptchaRetryTimeout = setTimeout(() => {
            this.recaptchaRetryTimeout = null
            if (this._isDestroyed) return
            this.recaptchaLoading = false
            if (this.showRecaptcha && !this.recaptchaSetupDone) {
              this.initRecaptcha()
            }
          }, RECAPTCHA_RETRY_DELAY_MS)
          return
        }

        this.recaptchaLoading = false
      }
    },
    async setupRecaptcha () {
      if (this.recaptchaVerifier) {
        this.recaptchaVerifier.clear()
        this.recaptchaVerifier = null
      }

      const recaptchaContainer = this.$refs.recaptchaContainer || document.getElementById('recaptcha-container-signup')
      if (!recaptchaContainer) {
        throw new Error('Recaptcha container not found.')
      }

      this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('recaptcha-container-signup', {
        size: 'normal',
        callback: () => {
          this.recaptchaVerified = true
        },
        'expired-callback': () => {
          this.recaptchaVerified = false
        }
      })
      this.recaptchaWidgetId = await this.recaptchaVerifier.render()
    },
    async handleSignup () {
      this.submitAttempted = true
      if (this.submitDisabled) return

      this.loading = true

      try {
        // Check disposable email
        const emailDomain = this.email.split('@')[1]
        if (disposableEmailBlocklist.has(emailDomain)) {
          this.emailIsDisposable = true
          throw new Error('Disposable email addresses are not allowed.')
        }

        // Validate signup
        const { error, message } = await ForeplayAPI.Users.validateSignup({ email: this.email })
        if (error) {
          this.$showAlert({
            type: 'error',
            message: message
          })
          this.recaptchaVerified = false
          this.recaptchaVerifier?.reset(this.recaptchaWidgetId)
          this.loading = false
          return
        }

        // Create Firebase user
        const result = await firebase.auth().createUserWithEmailAndPassword(this.email, this.password)
        
        await result.user.getIdToken()

        // Update profile
        await result.user.updateProfile({
          displayName: this.fullName
        })

        // Create Firestore user doc with TEAM status; onboarding completes after invite acceptance
        const userPayload = {
          first_name: this.fullName.split(' ')[0],
          email: result.user.email,
          name: this.fullName,
          trialStarted: Date.now(),
          onboardingStatus: 'TEAM'
        }

        if (this.fullName.split(' ')[1]) {
          userPayload.last_name = this.fullName.split(' ')[1]
        }

        await firebase.firestore().collection('auth-users').doc(result.user.uid).set(userPayload, { merge: true })

        // Emit success - parent handles invite acceptance
        this.$emit('auth-complete', { isNewUser: true })
      } catch (error) {
        console.error(error)
        this.$showAlert({
          message: error.message || 'An error occurred during signup.',
          type: 'error'
        })
        this.recaptchaVerified = false
        if (this.recaptchaVerifier) {
          this.recaptchaVerifier.reset(this.recaptchaWidgetId)
        }
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.input-container {
  background-color: #F9FAFB;
  box-shadow: 0 0 0 1px #E5E7EB;
  border-radius: 10px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  transition: background-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.input-container:hover {
  background-color: #F3F4F6;
  box-shadow: 0 0 0 1px #D1D5DB;
}
.input-container.active, .input-container.active:hover {
  background-color: white;
  /* box-shadow: 0 0 0 2px #0063F4; */
}
.input-container.errored, .input-container.active.errored, .input-container.active.errored:hover {
  background-color: white;
  box-shadow: 0 0 0 2px #F4003A;
}

.form-input {
  background-color: transparent;
  color: #111827;
  caret-color: #0063F4;
  max-width: none;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding: 0;
  margin: 0;
  border: none;
  font-size: inherit;
  outline: none;
}
.form-input:focus {
  outline: none;
  border: none;
  box-shadow: none;
}
.form-input::placeholder {
  transition: color 200ms ease-in-out;
  color: #9CA3AF;
}
.form-input:focus::placeholder {
  color: #D1D5DB;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus,
select:-webkit-autofill,
select:-webkit-autofill:hover,
select:-webkit-autofill:focus {
  -webkit-text-fill-color: #111827 !important;
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
}

.form-input:-moz-autofill {
  color: #111827 !important;
}

.form-input:autofill {
  color: #111827 !important;
}

.submit-button {
  background-color: #0063F4;
  color: white;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: background-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.submit-button:hover:not(.disabled) {
  background-color: #0052CC;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.submit-button.disabled {
  background-color: #E5E7EB;
  color: #9CA3AF;
  box-shadow: none;
  cursor: not-allowed;
}

.visibility-enter-active, .visibility-leave-active {
  transition: opacity 75ms ease-in-out;
  position: absolute;
  right: 0px;
  top: 4px;
}
.visibility-enter-from, .visiblity-enter, .visibility-leave-to {
  opacity: 0;
}
.visibility-enter-to, .visibility-leave-from {
  opacity: 1;
}

.v-enter-active, .v-leave-active {
  transition: opacity 75ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.error-enter-active {
  animation: errorEnter 75ms ease-in-out forwards;
}
.error-leave-active {
  animation: errorLeave 75ms ease-in-out forwards;
}
@keyframes errorEnter {
  0% { height: 0px; opacity: 0; }
  50% { opacity: 0; }
  100% { height: 28px; opacity: 1; }
}
@keyframes errorLeave {
  0% { height: 28px; opacity: 1; }
  50% { opacity: 0; }
  100% { height: 0px; opacity: 0; }
}

.recaptcha-enter-active {
  animation: recaptchaEnter 500ms ease-in-out 300ms forwards;
  animation-fill-mode: backwards;
}
.recaptcha-leave-active {
  animation: recaptchaLeave 500ms ease-in-out forwards;
}
@keyframes recaptchaEnter {
  0% { height: 0px; opacity: 0; }
  50% { height: 102px; opacity: 0; }
  100% { height: 102px; opacity: 1; }
}
@keyframes recaptchaLeave {
  0% { height: 102px; opacity: 1; }
  50% { height: 102px; opacity: 0; }
  100% { height: 0px; opacity: 0; }
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
